/**
 * Sidebar.tsx — Left navigation rail. Ported from prototype shell.jsx `Sidebar`.
 * Project card + switcher are wired to the real tender-api projects list;
 * sections without a backend carry an "In arrivo" tag instead of fake counts.
 * No numeric nav badges: the only org-wide count available (open
 * discrepancies) would sit on a per-project destination — wrong scope reads
 * as a data bug, so it lives on the Dashboard card instead.
 */
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import { I } from "../icons";
import { DropdownMenu } from "../ui/DropdownMenu";
import { projectEntryPath, SECTIONS, strategyPath, type StageId } from "../../lib/nav";
import { useProject, useProjects, type TenderProject } from "../../lib/tenderApi";

interface SidebarProps {
  active: StageId;
  visible: boolean;
}

/** Two-letter monogram for the project card ("Demo AOSTA" → "DA"). */
function monogram(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function Sidebar({ active, visible }: SidebarProps): JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const projectsQuery = useProjects();
  // Active project = the one in the route when on /audit/:projectId,
  // otherwise the most recently updated (the backend lists by updated_at desc).
  // The routed project may be absent from the default list (archived, or
  // beyond the list limit) — fetch it directly rather than showing a wrong one.
  /* `P-63` — the preparation screen carries a project id now, so the rail can
     read it instead of guessing. It previously fell through to `projects[0]`,
     the most-recently-updated project, which LOOKS right while you are working
     on that project and is simply wrong as soon as you reopen an older one:
     the card would name a different project than the page. */
  const routeProjectId =
    matchPath("/audit/:projectId", location.pathname)?.params.projectId ??
    /* `P-65` — Strategia carries an id now, so the rail must read it here too.
       Without this line the page would show AOSTA while the card beside it
       still named `projects[0]`, which is the exact disagreement the comment
       above describes, moved one screen along. */
    matchPath("/strategy/:projectId", location.pathname)?.params.projectId ??
    matchPath("/projects/:projectId/prepare", location.pathname)?.params.projectId ??
    matchPath("/projects/:projectId/documents", location.pathname)?.params.projectId ??
    null;
  const routeProjectQuery = useProject(routeProjectId);
  if (!visible) return null;

  const projects = projectsQuery.data ?? [];
  const activeProject: TenderProject | null = routeProjectId
    ? (projects.find((p) => p.id === routeProjectId) ?? routeProjectQuery.data ?? null)
    : (projects[0] ?? null);
  const cardLoading =
    projectsQuery.isLoading || (routeProjectId != null && routeProjectQuery.isLoading);

  const projectCard = (onClick?: () => void): JSX.Element => (
    <div
      className="card"
      onClick={onClick}
      style={{
        padding: 10,
        boxShadow: "none",
        borderColor: "var(--c-line)",
        cursor: "pointer",
        width: "100%",
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          color: "var(--c-ink-3)",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        Progetto attivo
      </div>
      {activeProject ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              flexShrink: 0,
              background: "var(--c-brand-soft)",
              color: "var(--c-brand-ink)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {monogram(activeProject.name)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: "var(--c-ink)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {activeProject.name}
            </div>
            <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 1 }}>
              {[
                activeProject.city,
                activeProject.surfaceM2 != null
                  ? `${activeProject.surfaceM2.toLocaleString("it-IT")} m²`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || activeProject.statusLabel}
            </div>
          </div>
          <I.ChevronD size={12} style={{ color: "var(--c-ink-3)" }} />
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "var(--c-ink-3)", lineHeight: 1.5 }}>
          {cardLoading
            ? "Caricamento…"
            : projectsQuery.isError
              ? "Impossibile caricare i progetti — riprova più tardi"
              : "Nessun progetto — creane uno dal Workspace"}
        </div>
      )}
    </div>
  );

  return (
    <aside
      style={{
        width: 240,
        flexShrink: 0,
        borderRight: "1px solid var(--c-line)",
        background: "var(--c-bg)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Project switcher — real projects, most recent first */}
      <div style={{ padding: "14px 12px", borderBottom: "1px solid var(--c-line)" }}>
        {projects.length > 0 ? (
          <DropdownMenu
            block
            width={260}
            header="Cambia progetto"
            items={[
              ...projects.slice(0, 5).map((p) => ({
                icon: I.Folder,
                label: p.name,
                active: activeProject?.id === p.id,
                onClick: () => navigate(projectEntryPath(p)),
              })),
              ...(projects.length > 5
                ? [
                    {
                      icon: I.Layers,
                      label: `Tutti i progetti (${projects.length})`,
                      onClick: () => navigate("/"),
                    },
                  ]
                : []),
            ]}
            renderTrigger={(toggle) => projectCard(toggle)}
          />
        ) : (
          projectCard(() => navigate("/"))
        )}
      </div>

      {/* Nav */}
      <nav style={{ padding: "14px 8px", flex: 1, overflowY: "auto" }}>
        {SECTIONS.map((sec) => (
          <div key={sec.label} style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 10.5,
                color: "var(--c-ink-3)",
                fontWeight: 600,
                letterSpacing: "0.08em",
                padding: "0 8px 6px",
              }}
            >
              {sec.label}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {sec.items.map((item) => {
                const Ico = item.icon;
                const isActive = active === item.id;
                // Audit and Strategia both carry the active project in the
                // URL so breadcrumb, sidebar card and page all agree on which
                // project is open. `P-65`: Strategia was left out of this when
                // Audit was given an id, so clicking it from AOSTA opened the
                // most-recently-updated project's margins instead — a
                // different project's money under the heading you clicked.
                const target = !activeProject
                  ? item.path
                  : item.id === "audit"
                    ? projectEntryPath(activeProject)
                    : item.id === "strategy"
                      ? strategyPath(activeProject.id)
                      : item.path;
                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(target)}
                    style={{
                      appearance: "none",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      height: 32,
                      padding: "0 10px",
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 500,
                      color: isActive ? "var(--c-brand-ink)" : "var(--c-ink-2)",
                      background: isActive ? "var(--c-brand-soft)" : "transparent",
                      transition: "background var(--tr-fast), color var(--tr-fast)",
                      fontFamily: "inherit",
                      textAlign: "left",
                      width: "100%",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = "rgba(20,22,28,0.04)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <Ico
                      size={15}
                      style={{
                        color: isActive ? "var(--c-brand)" : "var(--c-ink-3)",
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.comingSoon && (
                      <span
                        style={{
                          fontSize: 9.5,
                          fontWeight: 600,
                          letterSpacing: "0.04em",
                          color: "var(--c-ink-3)",
                          border: "1px solid var(--c-line)",
                          borderRadius: 999,
                          padding: "1px 6px",
                        }}
                      >
                        In arrivo
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
